/**
 * https://ionicframework.com/blog/a-state-management-pattern-for-ionic-react-with-react-hooks/
 */
import { PushState } from '../hooks/OneSignalHooks';
import { WP_Checklist, WP_ChecklistEntry } from '../model/checklist/checklist';
import {
  HouseArray,
  HousePageArray,
  NewsView,
  ServiceArray,
  User,
  WP_HouseRef,
  WP_HouseTerm,
  WP_NotifsArray,
  WP_ServiceTerm,
} from '../model/models';
import { WP_Parent_Tag, WP_Tag } from '../model/tags/tags';
import { Step } from '../model/wizard/wizard';
import { LogItem, LogLevel } from '../services/LogService';
import { ModelsService } from '../services/ModelsService';

export interface ChecklistEntryMap {
  [key: string]: WP_ChecklistEntry;
}

export interface GlobalState {
  showAnimSplash: boolean;
  showDebug: boolean;
  isOnline: boolean;
  isConnected: boolean;
  isAuthLoaded: boolean;
  isLoadingOfflineDatas: boolean;
  isLoadingOnlineDatas: boolean;
  allHouses: HouseArray;
  allServices: WP_ServiceTerm[];
  news: Map<string, NewsView>;
  mainHouse: WP_HouseTerm | undefined;
  housePages: HousePageArray;
  servicesPages: ServiceArray;
  user: User | undefined;
  house: WP_HouseRef | undefined;
  locale: string;
  isLoggerReady: boolean;
  logLevel: LogLevel;
  logs: LogItem[];
  pushState: PushState;
  allNotifs: WP_NotifsArray;
  nbUnreadNotifs: number;
  mustRefreshNotifs: boolean;
  allChecklists: Array<WP_Checklist>;
  allChecklistsEntries: ChecklistEntryMap;
  isChecklistsFetch: boolean;
  wizardSteps: Step[];
  cachedTags: Map<string, WP_Tag[]>;
  parentTags: WP_Parent_Tag[];
  placeTags: WP_Parent_Tag[];
  currentPath: string;
  previousPath: string;
  requestedPath: string;
}

/** Actions */
export enum ActionType {
  ShowAnimSplash,
  ShowDebug,
  Connect,
  Disconnect,
  SetLoadingOfflineDatas,
  SetLoadingOnlineDatas,
  SetOnline,
  SetAllHouses,
  SetAllServices,
  SetServicesPages,
  AddNews,
  AddEvent,
  SetUser,
  SetHousePages,
  SetLogLevel,
  AddLog,
  ClearLogs,
  SetPushState,
  SetAllNotifs,
  SetMustRefreshNotifs,
  SetAllChecklists,
  SetAllChecklistsEntries,
  SetWizardSteps,
  SetParentTags,
  SetCachedTags,
  SetPlaceTags,
  SetCurrentPath,
  SetRequestedPath,
}

export interface ShowAnimSplash {
  type: ActionType.ShowAnimSplash;
  payload: boolean;
}

export interface ShowDebug {
  type: ActionType.ShowDebug;
  payload: boolean;
}

export interface Connect {
  type: ActionType.Connect;
  payload: boolean;
}

export interface Disconnect {
  type: ActionType.Disconnect;
  payload: boolean;
}

export interface SetOnline {
  type: ActionType.SetOnline;
  payload: boolean;
}

export interface SetLoadingOfflineDatas {
  type: ActionType.SetLoadingOfflineDatas;
  payload: boolean;
}
export interface SetLoadingOnlineDatas {
  type: ActionType.SetLoadingOnlineDatas;
  payload: boolean;
}

export interface SetAllHouses {
  type: ActionType.SetAllHouses;
  payload: HouseArray;
}

export interface SetAllServices {
  type: ActionType.SetAllServices;
  payload: WP_ServiceTerm[];
}

export interface AddNews {
  type: ActionType.AddNews;
  payload: NewsView[];
}

export interface SetHousePages {
  type: ActionType.SetHousePages;
  payload: HousePageArray;
}

export interface SetServicesPages {
  type: ActionType.SetServicesPages;
  payload: ServiceArray;
}

export interface SetUser {
  type: ActionType.SetUser;
  payload: User | undefined;
}

export interface SetLogLevel {
  type: ActionType.SetLogLevel;
  payload: LogLevel;
}
export interface AddLog {
  type: ActionType.AddLog;
  payload: LogItem;
}

export interface ClearLogs {
  type: ActionType.ClearLogs;
}

export interface SetPushState {
  type: ActionType.SetPushState;
  payload: PushState;
}

export interface SetAllNotifs {
  type: ActionType.SetAllNotifs;
  payload: WP_NotifsArray;
}

export interface SetMustRefreshNotifs {
  type: ActionType.SetMustRefreshNotifs;
  payload: boolean;
}

export interface SetAllChecklistsEntries {
  type: ActionType.SetAllChecklistsEntries;
  payload: ChecklistEntryMap;
}

export interface SetAllChecklists {
  type: ActionType.SetAllChecklists;
  payload: Array<WP_Checklist>;
}

export interface SetWizardSteps {
  type: ActionType.SetWizardSteps;
  payload: Array<Step>;
}

export interface SetParentTags {
  type: ActionType.SetParentTags;
  payload: Array<WP_Parent_Tag>;
}

export interface SetCachedTags {
  type: ActionType.SetCachedTags;
  payload: { key: string; tags: Array<WP_Parent_Tag> };
}

export interface SetPlaceTags {
  type: ActionType.SetPlaceTags;
  payload: Array<WP_Parent_Tag>;
}

export interface SetCurrentPath {
  type: ActionType.SetCurrentPath;
  payload: { previousPath: string; currentPath: string };
}

export interface SetRequestedPath {
  type: ActionType.SetRequestedPath;
  payload: string;
}

export type Actions =
  | ShowAnimSplash
  | ShowDebug
  | Connect
  | Disconnect
  | SetOnline
  | SetLoadingOfflineDatas
  | SetLoadingOnlineDatas
  | SetAllHouses
  | SetAllServices
  | AddNews
  | SetUser
  | SetHousePages
  | SetServicesPages
  | SetLogLevel
  | AddLog
  | ClearLogs
  | SetAllNotifs
  | SetMustRefreshNotifs
  | SetPushState
  | SetAllChecklistsEntries
  | SetAllChecklists
  | SetWizardSteps
  | SetParentTags
  | SetCachedTags
  | SetPlaceTags
  | SetCurrentPath
  | SetRequestedPath;

const getInitialLogLevel = () => {
  const APP_ENV_LOG_LEVEL = {
    develop: 1, // LogLevel.info
    preprod: 2, // LogLevel.warn
    production: 2, // LogLevel.warn
    default: 2, // LogLevel.warn
  };
  const env = process?.env?.REACT_APP_ENV || 'default';
  return APP_ENV_LOG_LEVEL[env];
};

/* Initial State */
export const INITIAL_GLOBAL_STATE: GlobalState = {
  showAnimSplash: true,
  showDebug: false,
  isConnected: false,
  isAuthLoaded: false,
  isOnline: false,
  isLoadingOfflineDatas: false,
  isLoadingOnlineDatas: false,
  allHouses: [],
  allServices: [],
  news: new Map(),
  housePages: [],
  servicesPages: [],
  user: undefined,
  house: undefined,
  mainHouse: undefined,
  locale: 'en_US',
  isLoggerReady: false,
  logLevel: getInitialLogLevel(),
  logs: [],
  pushState: PushState.WAITING_FOR_INIT,
  allNotifs: [],
  nbUnreadNotifs: 0,
  mustRefreshNotifs: true,
  allChecklists: [],
  isChecklistsFetch: false,
  allChecklistsEntries: {},
  wizardSteps: [],
  parentTags: [],
  cachedTags: new Map(),
  placeTags: [],
  currentPath: '',
  previousPath: '',
  requestedPath: '/news',
};

export const fullInitialState = {
  ...INITIAL_GLOBAL_STATE,
  ...loadOnRefresh(),
};

/**
 * Filter the props we want to persist on refresh
 * WARNING: should not contain sensitiv data, as it use the localstorage.
 * @param state 
 * @returns 
 */
export function buildOnRefreshSubstate(state: any) {
  // copy some properties from state into a new object
  const substate = {};

  const addProp = (name) => {
    if( state[name] !== undefined ) {
      substate[name] = state[name];
    }
  }

  // persist logLevel
  addProp('logLevel');

  // persist requestedPath (used to redirect to the requested location after the login process)
  addProp('requestedPath');

  return substate;
}

export function persistOnRefresh(state: GlobalState) {
  localStorage.setItem('global-state', JSON.stringify(buildOnRefreshSubstate(state)));
}

export function loadOnRefresh() {
  const data = JSON.parse(localStorage.getItem('global-state') || '{}');
  return data ? buildOnRefreshSubstate(data) : {};
}

/* reducer */
export function globalStateReducer(
  state: GlobalState,
  action: Actions
): GlobalState {
  switch (action.type) {
    case ActionType.ShowAnimSplash:
      return { ...state, showAnimSplash: action.payload };

    case ActionType.ShowDebug:
      return { ...state, showDebug: action.payload };

    case ActionType.Connect:
      return { ...state, isConnected: true, isAuthLoaded: true };

    case ActionType.Disconnect:
      return {
        ...fullInitialState,
        isConnected: false,
        isAuthLoaded: true,
        isLoggerReady: state.isLoggerReady, // preserve logger state to prevent blank screen
        logLevel: state.logLevel, // preserve logger state to prevent blank screen
        showAnimSplash: state.showAnimSplash,
      };

    case ActionType.SetLoadingOfflineDatas:
      return {
        ...state,
        isLoadingOfflineDatas: action.payload,
      };

    case ActionType.SetLoadingOnlineDatas:
      return {
        ...state,
        isLoadingOnlineDatas: action.payload,
      };

    case ActionType.SetAllHouses:
      return {
        ...state,
        allHouses: action.payload,
        mainHouse: action.payload.filter(function (house: WP_HouseTerm) {
          return house.acf.main;
        })[0],
      };

    case ActionType.SetAllServices:
      return {
        ...state,
        allServices: action.payload,
      };

    case ActionType.AddNews:
      const cloneNews = new Map<string, NewsView>(
        JSON.parse(JSON.stringify(Array.from(state.news)))
      );
      action.payload.map((n) => {
        cloneNews.set(n.slug, { ...n });
        return true;
      });
      return {
        ...state,
        news: cloneNews,
      };

    case ActionType.SetHousePages:
      return { ...state, housePages: action.payload };

    case ActionType.SetServicesPages:
      return { ...state, servicesPages: action.payload };

    case ActionType.SetUser:
      const house = ModelsService.getUserHouseRef(action.payload);
      return {
        ...state,
        user: action.payload,
        house: house,
        locale: action.payload?.acf.locale || 'en_US',
      };

    case ActionType.SetOnline:
      return { ...state, isOnline: action.payload };

    case ActionType.AddLog:
      return { ...state, logs: [...state.logs, action.payload].slice(-500) };

    case ActionType.ClearLogs:
      return { ...state, logs: [] };

    case ActionType.SetLogLevel:
      return { ...state, logLevel: action.payload, isLoggerReady: true };

    case ActionType.SetPushState:
      return { ...state, pushState: action.payload };

    case ActionType.SetAllNotifs:
      return {
        ...state,
        allNotifs: action.payload,
        nbUnreadNotifs: action.payload.filter(
          (notif) => notif.acf.state === 'unread'
        ).length,
      };

    case ActionType.SetMustRefreshNotifs:
      return { ...state, mustRefreshNotifs: action.payload };

    case ActionType.SetAllChecklists:
      return {
        ...state,
        allChecklists: action.payload,
        isChecklistsFetch: true,
      };

    case ActionType.SetAllChecklistsEntries:
      return {
        ...state,
        allChecklistsEntries: {
          ...state.allChecklistsEntries,
          ...action.payload,
        },
      };

    case ActionType.SetWizardSteps:
      return {
        ...state,
        wizardSteps: action.payload,
      };

    case ActionType.SetParentTags:
      return {
        ...state,
        parentTags: action.payload,
      };

    case ActionType.SetCachedTags:
      const clone = new Map<string, WP_Tag[]>(
        JSON.parse(JSON.stringify(Array.from(state.cachedTags)))
      );
      clone.set(action.payload.key, [...action.payload.tags]);
      return {
        ...state,
        cachedTags: clone,
      };

    case ActionType.SetPlaceTags:
      return {
        ...state,
        placeTags: action.payload,
      };

    case ActionType.SetCurrentPath:
      return {
        ...state,
        currentPath: action.payload.currentPath,
        previousPath: action.payload.previousPath,
      };

    case ActionType.SetRequestedPath:
      return {
        ...state,
        requestedPath: action.payload,
      };
  }
}
